import React from 'react';
import { getContactRequestStatusConfig } from '../../hooks/useViewContactRequest';

const PLACEHOLDER = '—';

const TIMELINE_COLOR = '#FFCC00';

/**
 * Bordered content card with a section heading — the standard container for a
 * detail tab's sections. Matches the service-request view's SectionCard so the
 * two detail pages share the same spacing and typography.
 */
export const SectionCard: React.FC<{
  title: string;
  children: React.ReactNode;
  testId?: string;
  className?: string;
}> = ({ title, children, testId, className }) => (
  <div
    className={`border border-[#DCDDE0] rounded-lg p-4   flex flex-col gap-4${
      className ? ` ${className}` : ''
    }`}
    {...(testId ? { 'data-testid': testId } : {})}
  >
    <p className="text-titleDefault font-medium text-on-surface-variant">
      {title}
    </p>
    {children}
  </div>
);

/** Read-only label+value pair used throughout the detail tabs. */
export const InfoField: React.FC<{
  label: string;
  value: string | null | undefined;
  fullWidth?: boolean;
  testId?: string;
}> = ({ label, value, fullWidth = false, testId }) => (
  <div
    className={`flex min-w-0 flex-col gap-2${fullWidth ? ' col-span-full' : ''}`}
    {...(testId ? { 'data-testid': testId } : {})}
  >
    <h2 className="text-bodySmall font-normal text-on-surface-variant">
      {label}
    </h2>
    <p
      className="min-w-0 wrap-break-word text-bodyLarge font-normal text-neutral-10"
      {...(testId ? { 'data-testid': `${testId}-value` } : {})}
    >
      {value || PLACEHOLDER}
    </p>
  </div>
);

/** Coloured pill badge showing the human-readable label for a contact request status. */
export const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg = getContactRequestStatusConfig(status);
  return (
    <span
      className="inline-flex items-center gap-2 px-3 py-1 rounded-lg whitespace-nowrap"
      style={{
        backgroundColor: cfg.bg,
        color: cfg.text,
        fontSize: 14,
        fontWeight: 500,
        lineHeight: '20px',
        letterSpacing: '0.1px',
      }}
      data-testid={`view-cr-status-${status}`}
    >
      <span
        className="rounded-full shrink-0"
        style={{ width: 6, height: 6, backgroundColor: cfg.text }}
      />
      {cfg.label}
    </span>
  );
};

function getInitials(name: string | null | undefined): string {
  if (!name) {
    return '';
  }
  return name
    .trim()
    .split(/\s+/)
    .map((n) => n[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * 48px dashed ring + 40px avatar for a history-timeline entry. Contact-request
 * history actors have no profile image, so this always renders initials.
 */
export const TimelineAvatarCircle: React.FC<{ name: string | null }> = ({
  name,
}) => (
  <div
    data-testid="cr-timeline-avatar-circle"
    style={{ position: 'relative', width: 48, height: 48, flexShrink: 0 }}
  >
    <svg
      width="48"
      height="48"
      style={{ position: 'absolute', top: 0, left: 0 }}
    >
      <circle
        cx="24"
        cy="24"
        r="23"
        fill="none"
        stroke={TIMELINE_COLOR}
        strokeWidth="1"
        strokeDasharray="4 6"
        strokeLinecap="round"
      />
    </svg>
    <div
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 40,
        height: 40,
        borderRadius: '50%',
        backgroundColor: '#E8EEF0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <span style={{ color: '#223746', fontSize: 14, fontWeight: 500 }}>
        {getInitials(name)}
      </span>
    </div>
  </div>
);

/** Dashed vertical connector with a centred chevron between timeline entries. */
export const TimelineConnector: React.FC<{
  bgColor?: string;
  testId?: string;
}> = ({ bgColor = '#ffffff', testId }) => {
  const lineStyle: React.CSSProperties = {
    flex: 1,
    width: 1,
    minHeight: 12,
    background: `repeating-linear-gradient(to bottom, ${TIMELINE_COLOR} 0px, ${TIMELINE_COLOR} 6px, transparent 6px, transparent 12px)`,
  };

  return (
    <div
      data-testid={testId}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: 1,
        minHeight: 56,
        marginTop: 2,
      }}
    >
      <div style={lineStyle} />
      <div
        style={{
          background: bgColor,
          padding: '4px 8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: 14,
            height: 14,
            borderTop: `1px solid ${TIMELINE_COLOR}`,
            borderLeft: `1px solid ${TIMELINE_COLOR}`,
            transform: 'rotate(45deg)',
          }}
        />
      </div>
      <div style={lineStyle} />
    </div>
  );
};
